<template>
  <V2GuestLayout title="Login" subtitle="Login to your account">
    <div
      class="mx-5 flex flex-1 flex-col items-center justify-center gap-6 md:flex-grow-0 md:gap-0"
    >
      <div class="w-full max-w-[460px] space-y-4 md:mt-4">
        <form @submit.prevent="handleLogin" class="space-y-16">
          <div class="space-y-4">
            <SWFormInput
              v-model="form.email"
              label="Email"
              type="email"
              :left-icon="['fal', 'envelope']"
              :error="form.errors.email"
              :disabled="form.processing || integrating"
            />
            <SWFormInput
              v-model="form.password"
              label="Password"
              :type="!allowPasswordReveal ? 'password' : reveal ? 'text' : 'password'"
              :left-icon="['fal', 'lock']"
              :error="form.errors.password"
              :disabled="form.processing || integrating"
            >
              <template #right v-if="allowPasswordReveal">
                <span
                  class="cursor-pointer text-gray-500 hover:text-black disabled:text-gray-400"
                  @click="reveal = !reveal"
                  :disabled="form.processing || integrating"
                >
                  <FontAwesomeIcon
                    :icon="['fal', reveal ? 'eye' : 'eye-slash']"
                  />
                </span>
              </template>
            </SWFormInput>
            <div class="flex items-center justify-between pb-5">
              <div class="flex items-center gap-3">
                <input
                  v-model="form.remember"
                  :disabled="form.processing || integrating"
                  id="sw-remember-me"
                  type="checkbox"
                  class="h-4 w-4 rounded-sm border-gray-300 bg-gray-100 text-primary focus:ring-2 focus:ring-primary"
                />
                <label for="sw-remember-me" class="text-sm">
                  Remember Me
                </label>
              </div>
              <Link
                href="/password/forgot"
                class="text-sm font-medium hover:text-primary"
              >
                Forgot Password?
              </Link>
            </div>

            <SWFormButton
              color="primary"
              label="Login"
              :size="isMobile ? 'md' : 'lg'"
              type="submit"
              block
              :loading="form.processing"
              :disabled="form.processing || integrating"
            />
          </div>
        </form>
      </div>

      <div
        class="md:flex-0 mb-8 flex flex-col justify-end gap-4 text-center md:justify-start md:pt-5"
      >
        <!-- <div class="md:hidden"></div> -->
        <p class="w-64 text-sm text-gray-500">
          Don't have an account?
          <Link
            class="cursor-pointer font-medium text-black hover:text-primary dark:text-white dark:hover:text-primary"
            href="/register"
          >
            Register
          </Link>
        </p>
      </div>

      <div class="relative w-full max-w-[460px] space-y-4 md:my-4">
        <Separator label="Or" />
      </div>
      <div class="relative flex w-full max-w-[460px] flex-col gap-3 md:my-4">
        <GoogleButton label="Sign in with Google" />
        <MicrosoftButton label="Sign in with Microsoft" />
        <LinkedInButton label="Sign in with LinkedIn" />
      </div>
    </div>
  </V2GuestLayout>
</template>

<script setup lang="ts">
import GoogleButton from '@/Components/GoogleButton/index.vue';
import MicrosoftButton from '@/Components/MicrosoftButton/index.vue';
import { useIsMobile } from '@/Composables/app/useIsMobile';
import { useAuthenticatorStore } from '@/stores/guest/authenticator';
import { PageProps } from '@/types';
import { Link, useForm, usePage } from '@inertiajs/vue3';
import { Crisp } from 'crisp-sdk-web';
import { storeToRefs } from 'pinia';
import { onMounted, onUnmounted, ref, watch } from 'vue';

Crisp.configure('7c524df6-e037-4b92-b6b8-a6c1e839cfa0');
const { isMobile } = useIsMobile();

const page = usePage<PageProps<{ redirect?: string }>>();
/**
 * Allow password reveal for the password input field
 * Set it to "true" if Dale wants to allow password reveal for the password input field
 */
const allowPasswordReveal = ref(false);

const form = useForm({
  email: '',
  password: '',
  remember: true,
});

watch(
  () => [form.email, form.password],
  () => {
    form.clearErrors('email');
  },
);

const store = useAuthenticatorStore();
const { integrating } = storeToRefs(store);
const reveal = ref(false);

const handleLogin = async () => {
  form
    .transform((data) => {
      return {
        ...data,
        redirect: page.props.redirect,
      };
    })
    .post('/login');
};

onMounted(() => {
  Crisp.chat.show();
});

onUnmounted(() => {
  store.$reset();
  Crisp.chat.hide();
});
</script>
